import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/authStore'
import type { CateringOrder, CateringOrderItem, CateringItemPricing, CateringOrderRevision } from '@/types/app'

export function useCateringOrders() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: queryKeys.catering(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('catering_orders')
        .select('*, items:catering_order_items(*)')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as CateringOrder[]
    },
    enabled: !!restaurantId,
  })
}

// Lightweight badge count for nav — deliberately a head-count query, not the
// full useCateringOrders() list, since this renders on every page of the app.
export function useCateringPendingUpdatesCount() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: ['catering', restaurantId, 'pending-updates-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('catering_orders')
        .select('id', { count: 'exact', head: true })
        .eq('restaurant_id', restaurantId)
        .eq('has_customer_update', true)
      if (error) throw error
      return count ?? 0
    },
    enabled: !!restaurantId,
    refetchInterval: 60_000,
  })
}

// Clears the "customer updated this" flag when staff open an order — a plain
// flag flip, deliberately not routed through useSaveCateringOrder so it never
// touches items or writes a revision snapshot.
export function useMarkCateringUpdateSeen() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase
        .from('catering_orders')
        .update({ has_customer_update: false })
        .eq('id', orderId)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.catering(restaurantId) })
      qc.invalidateQueries({ queryKey: ['catering', restaurantId, 'pending-updates-count'] })
    },
  })
}

export function useCateringOrder(id: string) {
  return useQuery({
    queryKey: queryKeys.cateringOrder(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('catering_orders')
        .select('*, items:catering_order_items(*)')
        .eq('id', id)
        .single()
      if (error) throw error
      return data as CateringOrder
    },
    enabled: !!id,
  })
}

type SaveOrderPayload = {
  order: Omit<Partial<CateringOrder>, 'items' | 'order_number'>
  items: Omit<CateringOrderItem, 'id' | 'catering_id'>[]
}

export function useSaveCateringOrder() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ order, items }: SaveOrderPayload) => {
      if (order.id) {
        // Snapshot what's about to be replaced before touching it, so staff can
        // always see how an order got to its current state without the item
        // history itself getting harder to manage than the POS was.
        const { data: prevItems } = await supabase
          .from('catering_order_items')
          .select('name, tray_size, quantity, unit_price, notes')
          .eq('catering_id', order.id)
        if (prevItems && prevItems.length > 0) {
          const { data: prevOrder } = await supabase
            .from('catering_orders').select('quote_amount').eq('id', order.id).single()
          const changer = useAuthStore.getState().user
          await supabase.from('catering_order_revisions').insert({
            restaurant_id:    restaurantId,
            catering_id:      order.id,
            changed_by:       'staff',
            changed_by_name:  changer?.full_name ?? null,
            quote_amount:     prevOrder?.quote_amount ?? null,
            items:            prevItems,
          })
        }

        const { data, error } = await supabase
          .from('catering_orders')
          .update({
            client_name:          order.client_name,
            client_phone:         order.client_phone ?? '',
            client_email:         order.client_email ?? null,
            client_company:       order.client_company ?? null,
            event_date:           order.event_date,
            event_time:           order.event_time ?? null,
            event_type:           order.event_type ?? null,
            guest_count:          order.guest_count ?? null,
            venue_address:        order.venue_address ?? null,
            venue_notes:          order.venue_notes ?? null,
            event_sections:            order.event_sections ?? [],
            fulfillment_type:          order.fulfillment_type ?? null,
            delivery_fee:              order.delivery_fee ?? null,
            delivery_is_complimentary: order.delivery_is_complimentary ?? false,
            discount_amount:      order.discount_amount ?? null,
            discount_percentage:  order.discount_percentage ?? null,
            status:               order.status,
            quote_amount:         order.quote_amount ?? null,
            deposit_percentage:   order.deposit_percentage ?? 25,
            deposit_amount:       order.deposit_amount ?? null,
            deposit_paid_at:      order.deposit_paid_at ?? null,
            balance_due:          order.balance_due ?? null,
            balance_paid_at:      order.balance_paid_at ?? null,
            special_requests:     order.special_requests ?? null,
            dietary_requirements: order.dietary_requirements ?? null,
            notes:                order.notes ?? null,
            internal_notes:       order.internal_notes ?? null,
            assigned_to:          order.assigned_to ?? null,
            updated_at:           new Date().toISOString(),
          })
          .eq('id', order.id)
          .select()
          .single()
        if (error) throw error

        await supabase.from('catering_order_items').delete().eq('catering_id', order.id)
        if (items.length > 0) {
          const { error: iErr } = await supabase
            .from('catering_order_items')
            .insert(items.map((it, idx) => ({ ...it, catering_id: order.id, sort_order: it.sort_order ?? idx })))
          if (iErr) throw iErr
        }
        return data as CateringOrder
      }

      const { data, error } = await supabase
        .from('catering_orders')
        .insert({
          restaurant_id:        restaurantId,
          client_name:          order.client_name ?? '',
          client_phone:         order.client_phone ?? '',
          client_email:         order.client_email ?? null,
          client_company:       order.client_company ?? null,
          event_date:           order.event_date ?? new Date().toISOString().split('T')[0],
          event_time:           order.event_time ?? null,
          event_type:           order.event_type ?? null,
          guest_count:          order.guest_count ?? null,
          venue_address:        order.venue_address ?? null,
          venue_notes:          order.venue_notes ?? null,
          event_sections:            order.event_sections ?? [],
          fulfillment_type:          order.fulfillment_type ?? null,
          delivery_fee:              order.delivery_fee ?? null,
          delivery_is_complimentary: order.delivery_is_complimentary ?? false,
          discount_amount:      order.discount_amount ?? null,
          discount_percentage:  order.discount_percentage ?? null,
          status:               order.status ?? 'inquiry',
          quote_amount:         order.quote_amount ?? null,
          deposit_percentage:   order.deposit_percentage ?? 25,
          deposit_amount:       order.deposit_amount ?? null,
          deposit_paid_at:      order.deposit_paid_at ?? null,
          balance_due:          order.balance_due ?? null,
          balance_paid_at:      order.balance_paid_at ?? null,
          special_requests:     order.special_requests ?? null,
          dietary_requirements: order.dietary_requirements ?? null,
          notes:                order.notes ?? null,
          internal_notes:       order.internal_notes ?? null,
          assigned_to:          order.assigned_to ?? null,
          source:               'manual',
        })
        .select()
        .single()
      if (error) throw error

      const newId = (data as CateringOrder).id
      if (items.length > 0) {
        const { error: iErr } = await supabase
          .from('catering_order_items')
          .insert(items.map((it, idx) => ({ ...it, catering_id: newId, sort_order: it.sort_order ?? idx })))
        if (iErr) throw iErr
      }
      return data as CateringOrder
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.catering(restaurantId) })
      qc.invalidateQueries({ queryKey: queryKeys.cateringOrder(data.id) })
      qc.invalidateQueries({ queryKey: queryKeys.cateringRevisions(data.id) })
    },
  })
}

export function useCateringOrderRevisions(cateringId: string) {
  return useQuery({
    queryKey: queryKeys.cateringRevisions(cateringId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('catering_order_revisions')
        .select('*')
        .eq('catering_id', cateringId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as CateringOrderRevision[]
    },
    enabled: !!cateringId,
  })
}

export function useUpdateCateringStatus() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status, extra }: {
      id: string
      status: CateringOrder['status']
      extra?: Partial<Pick<CateringOrder, 'deposit_paid_at' | 'balance_paid_at' | 'internal_notes'>>
    }) => {
      const patch: Record<string, unknown> = { status, updated_at: new Date().toISOString(), ...extra }
      if (status === 'deposit_paid' && !extra?.deposit_paid_at) patch.deposit_paid_at = new Date().toISOString()
      if (status === 'completed'    && !extra?.balance_paid_at) patch.balance_paid_at = new Date().toISOString()
      const { data, error } = await supabase
        .from('catering_orders').update(patch).eq('id', id).select().single()
      if (error) throw error
      return data as CateringOrder
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.catering(restaurantId) })
      qc.invalidateQueries({ queryKey: queryKeys.cateringOrder(data.id) })
    },
  })
}

// ── Catering Pricing ────────────────────────────────────────────────────────

export function useCateringPricing() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: queryKeys.cateringPricing(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('catering_item_pricing')
        .select('*')
        .eq('restaurant_id', restaurantId)
      if (error) throw error
      const map = new Map<string, CateringItemPricing>()
      for (const row of (data ?? []) as CateringItemPricing[]) {
        map.set(row.menu_item_id, row)
      }
      return map
    },
    enabled: !!restaurantId,
  })
}

export function useSaveCateringPricing() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Omit<Partial<CateringItemPricing>, 'id'> & { menu_item_id: string }) => {
      const { data, error } = await supabase
        .from('catering_item_pricing')
        .upsert({ ...payload, restaurant_id: restaurantId, updated_at: new Date().toISOString() },
          { onConflict: 'restaurant_id,menu_item_id' })
        .select()
        .single()
      if (error) throw error
      return data as CateringItemPricing
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.cateringPricing(restaurantId) }),
  })
}
